import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { formatDistanceToNow, format } from "date-fns";
import {
  ResponsiveContainer, LineChart, Line,
} from "recharts";
import {
  ArrowLeft, Play, Trash2, Loader2, Plus, Clock, Mail, MessageSquare, Webhook,
  CheckCircle2, XCircle, Pause, PlayCircle, BarChart3,
} from "lucide-react";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ChartRenderer } from "@/components/chat/ChartRenderer";
import { ScheduleForm, DEFAULT_SCHEDULE, toSchedulePayload } from "@/components/reports/ScheduleForm";
import { EvidenceTrigger } from "@/components/evidence/EvidencePanel";
import { REPORTS } from "@/constants/testIds";
import { describeCron, detectTimezone } from "@/lib/cron";
import api from "@/lib/api";

const CHANNEL_ICON = { email: Mail, slack: MessageSquare, webhook: Webhook };

function rel(ts) {
  if (!ts) return "never";
  try { return formatDistanceToNow(new Date(ts), { addSuffix: true }); } catch { return "—"; }
}
function abs(ts) {
  if (!ts) return "—";
  try { return format(new Date(ts), "MMM d, HH:mm"); } catch { return "—"; }
}

function Sparkline({ runs }) {
  const data = [...runs].reverse().map((r, i) => ({ i, v: r.row_count || 0 }));
  if (data.length < 2) return null;
  return (
    <div className="h-10 w-28">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <Line type="monotone" dataKey="v" stroke="#0FAE96" strokeWidth={1.5} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function DeliveryPills({ ds }) {
  if (!ds?.length) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1.5">
      {ds.map((d, i) => {
        const ok = d.status === "sent";
        const skip = d.status === "skipped";
        return (
          <span key={i} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] ${
            ok ? "bg-emerald-500/10 text-emerald-400" : skip ? "bg-muted/40 text-muted-foreground" : "bg-red-500/10 text-red-400"
          }`}>
            {ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
            {d.channel} · {d.status}{d.error ? ` (${String(d.error).slice(0, 60)})` : ""}
          </span>
        );
      })}
    </div>
  );
}

function ScheduleRow({ report, sched, onChanged }) {
  const Icon = CHANNEL_ICON[sched.delivery] || Webhook;
  const paused = sched.status !== "active";

  const togglePause = async () => {
    await api.patch(`/reports/${report.id}/schedules/${sched.id}`, { status: paused ? "active" : "paused" });
    toast.success(paused ? "Schedule resumed" : "Schedule paused");
    onChanged();
  };
  const remove = async () => {
    await api.delete(`/reports/${report.id}/schedules/${sched.id}`);
    toast.success("Schedule removed");
    onChanged();
  };

  return (
    <div data-testid={REPORTS.scheduleRow} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border/50 bg-card/40 px-4 py-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-sm">
          <Icon className="h-4 w-4 text-accent" />
          <span>{describeCron(sched.cron)}</span>
          <Badge variant="outline" className={`text-[10px] ${paused ? "text-amber-400 border-amber-500/40" : "text-accent border-accent/40"}`}>
            {sched.status}
          </Badge>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {sched.timezone} · {sched.delivery}
          {sched.recipients?.length ? ` → ${sched.recipients.join(", ")}` : ""}
          {sched.slack_webhook_url ? " → Slack webhook" : ""}
          {sched.webhook_url ? ` → ${sched.webhook_url}` : ""}
        </p>
        <p className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
          <Clock className="h-3 w-3" /> next {rel(sched.next_run_at)} · {abs(sched.next_run_at)}
        </p>
      </div>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="sm" data-testid={REPORTS.pauseSchedule} onClick={togglePause} className="h-8 text-[11px]">
          {paused ? <PlayCircle className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
        </Button>
        <Button variant="ghost" size="sm" data-testid={REPORTS.deleteSchedule} onClick={remove} className="h-8 text-red-400 hover:text-red-300">
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default function ReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [runs, setRuns] = useState([]);
  const [running, setRunning] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newSchedule, setNewSchedule] = useState({ ...DEFAULT_SCHEDULE, timezone: detectTimezone() });
  const [savingSchedule, setSavingSchedule] = useState(false);

  const load = useCallback(async () => {
    const [{ data: rep }, { data: rs }] = await Promise.all([
      api.get(`/reports/${id}`),
      api.get(`/reports/${id}/runs`),
    ]);
    setReport(rep);
    setRuns(rs);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/reports/${id}/run`);
      if (data.status === "success") {
        const sent = (data.delivery_status || []).filter((d) => d.status === "sent").length;
        toast.success("Report ran", { description: sent ? `Delivered to ${sent} destination(s).` : "Executed — see history." });
      } else {
        toast.error("Run failed", { description: data.error });
      }
      await load();
    } finally {
      setRunning(false);
    }
  };

  const deleteReport = async () => {
    await api.delete(`/reports/${id}`);
    toast.success("Report deleted");
    navigate("/reports");
  };

  const addSchedule = async () => {
    setSavingSchedule(true);
    try {
      await api.post(`/reports/${id}/schedules`, toSchedulePayload(newSchedule));
      toast.success("Schedule added");
      setAdding(false);
      setNewSchedule({ ...DEFAULT_SCHEDULE, timezone: detectTimezone() });
      await load();
    } catch (e) {
      toast.error("Couldn't add schedule", { description: e.response?.data?.detail || e.message });
    } finally {
      setSavingSchedule(false);
    }
  };

  if (!report) {
    return (
      <div className="min-h-screen bg-background">
        <WorkspaceNav />
        <div className="px-6 py-20 text-center text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  const latestChart = report.last_run?.chart || runs[0]?.chart;

  return (
    <div data-testid={REPORTS.detailRoot} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <main className="mx-auto w-full max-w-6xl px-6 py-8">
        <Link to="/reports" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> Reports
        </Link>

        <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{report.name}</h1>
            <p className="mt-1.5 text-sm text-muted-foreground">{report.prompt}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid={REPORTS.runNow}
              onClick={runNow}
              disabled={running}
              className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
            >
              {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
              Run now
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" data-testid={REPORTS.deleteReport} className="rounded-full border-red-500/30 text-red-400 hover:bg-red-500/10">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="font-mono">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this report?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This removes the report, its schedules, and run history. This can’t be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={deleteReport} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Left: chart + history */}
          <div className="space-y-8 lg:col-span-2">
            <section>
              <h2 className="mb-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Latest result</h2>
              <div className="rounded-xl border border-border/60 bg-card/30 p-4">
                {latestChart ? (
                  <ChartRenderer chart={latestChart} />
                ) : (
                  <div className="py-12 text-center text-sm text-muted-foreground">
                    <BarChart3 className="mx-auto mb-3 h-8 w-8 text-accent" />
                    Run this report to generate its first chart.
                  </div>
                )}
              </div>
            </section>

            <section>
              <h2 className="mb-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Run history</h2>
              <div className="rounded-xl border border-border/60 bg-card/30">
                {runs.length ? runs.map((run) => (
                  <div key={run.id} data-testid={REPORTS.runHistoryRow}
                    className="flex flex-wrap items-center justify-between gap-3 border-b border-border/40 px-4 py-3 last:border-0">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-sm">
                        {run.status === "success"
                          ? <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                          : <XCircle className="h-4 w-4 text-red-400" />}
                        <span>{abs(run.ran_at)}</span>
                        <span className="text-xs text-muted-foreground">· {run.row_count} rows · {rel(run.ran_at)}</span>
                      </div>
                      {run.error && <p className="mt-1 pl-6 text-xs text-red-400">{run.error}</p>}
                      <div className="pl-6"><DeliveryPills ds={run.delivery_status} /></div>
                    </div>
                    <div className="flex items-center gap-2">
                      {run.evidence_answer_id && (
                        <EvidenceTrigger answerId={run.evidence_answer_id} label="Evidence" />
                      )}
                      <Sparkline runs={runs} />
                    </div>
                  </div>
                )) : (
                  <div className="px-4 py-10 text-center text-sm text-muted-foreground">No runs yet.</div>
                )}
              </div>
            </section>
          </div>

          {/* Right: schedules */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Schedules</h2>
              {!adding && (
                <Button variant="outline" size="sm" data-testid={REPORTS.addScheduleBtn} onClick={() => setAdding(true)}
                  className="h-7 rounded-full text-[11px]">
                  <Plus className="mr-1 h-3.5 w-3.5" /> Add
                </Button>
              )}
            </div>

            <div className="space-y-3">
              {(report.schedules || []).map((s) => (
                <ScheduleRow key={s.id} report={report} sched={s} onChanged={load} />
              ))}
              {!report.schedules?.length && !adding && (
                <p className="rounded-lg border border-dashed border-border/60 px-4 py-6 text-center text-xs text-muted-foreground">
                  No schedules. Add one to auto-deliver this report.
                </p>
              )}
            </div>

            {adding && (
              <div className="rounded-xl border border-border/60 bg-card/40 p-4">
                <ScheduleForm value={newSchedule} onChange={setNewSchedule} />
                <div className="mt-4 flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => setAdding(false)} className="rounded-full">Cancel</Button>
                  <Button size="sm" onClick={addSchedule} disabled={savingSchedule}
                    className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90">
                    {savingSchedule ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    Save schedule
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
